/**
 * vmx-supp-facts.js
 * Renders REAL supplement-facts data (assets/vmx-supp-facts.json) into the
 * page. Products carry no Shopify metafields, so nutrition + ingredient data
 * is sourced from this one JSON, fetched once and cached (mirrors the
 * vmx-reviews-data.json pattern). All values trace to physical pouch labels.
 *
 * Render targets (data-attribute → handle):
 *   [data-supp-facts="<handle>"]      → full Supplement Facts table
 *   [data-supp-ingredient="<handle>"] → headline active card (name, dose, blurb)
 *   [data-supp-directions="<handle>"] → directions paragraph
 *
 * No innerHTML for label values — DOM methods + textContent only.
 */
(function () {
  var SRC = window.VMX_SUPP_FACTS_SRC;
  if (!SRC) return;

  var _cache = null;
  function load() {
    if (_cache) return _cache;
    _cache = fetch(SRC, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .catch(function () { return {}; });
    return _cache;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function renderFacts(node, data) {
    var d = data[node.getAttribute('data-supp-facts')];
    if (!d) { node.hidden = true; return; }

    var meta = el('p', 'vmx-supp__meta');
    meta.appendChild(el('span', 'vmx-supp__serving', d.serving));
    if (d.servings_per_pack) {
      meta.appendChild(el('span', 'vmx-supp__sep', ' · '));
      meta.appendChild(el('span', null, d.servings_per_pack + ' servings per pack'));
    }
    if (d.form) {
      meta.appendChild(el('span', 'vmx-supp__sep', ' · '));
      meta.appendChild(el('span', 'vmx-supp__form', d.form));
    }
    node.appendChild(meta);

    var table = el('table', 'vmx-supp__table');
    var thead = table.createTHead();
    var hr = thead.insertRow();
    ['Active', 'Amount', '%NRV'].forEach(function (h) {
      var th = document.createElement('th');
      th.scope = 'col';
      th.textContent = h;
      hr.appendChild(th);
    });
    var tbody = table.createTBody();
    (d.actives || []).forEach(function (a, i) {
      var tr = tbody.insertRow();
      if (i === 0) tr.className = 'is-hero';
      var c0 = tr.insertCell();
      c0.appendChild(el('strong', null, a.name));
      if (a.form) c0.appendChild(el('em', 'vmx-supp__form-note', a.form));
      var c1 = tr.insertCell();
      c1.className = 'vmx-supp__amt';
      c1.textContent = a.amount || '';
      var c2 = tr.insertCell();
      c2.className = 'vmx-supp__nrv';
      c2.textContent = a.nrv || '—';
    });
    table.appendChild(tbody);
    node.appendChild(table);

    if (d.other) {
      var other = el('p', 'vmx-supp__other');
      other.appendChild(el('strong', null, 'Other ingredients: '));
      other.appendChild(document.createTextNode(d.other));
      node.appendChild(other);
    }
    var foot = el('p', 'vmx-supp__foot', '%NRV = EU Nutrient Reference Value. † = NRV not established.');
    node.appendChild(foot);
  }

  function renderIngredient(node, data) {
    var d = data[node.getAttribute('data-supp-ingredient')];
    if (!d || !d.headline) { node.hidden = true; return; }
    var h = d.headline;
    node.appendChild(el('span', 'vmx-suppcard__dose', h.dose));
    node.appendChild(el('span', 'vmx-suppcard__name', h.name));
    if (h.blurb) node.appendChild(el('span', 'vmx-suppcard__blurb', h.blurb));
    if (d.form || d.flavour) {
      var tag = el('span', 'vmx-suppcard__tag');
      var bits = [];
      if (d.form) bits.push(d.form);
      if (d.flavour) bits.push(d.flavour);
      tag.textContent = bits.join(' · ');
      node.appendChild(tag);
    }
  }

  function renderDirections(node, data) {
    var d = data[node.getAttribute('data-supp-directions')];
    if (!d || !d.directions) { node.hidden = true; return; }
    node.textContent = d.directions;
  }

  /* GB/EU authorised health claims, by nutrient. Only vitamins & minerals at a
     qualifying level carry authorised claims; botanicals, amino acids and
     probiotic strains do not, so they are deliberately absent here. */
  var CLAIMS = {
    'Vitamin C': 'contributes to normal immune function and the reduction of tiredness and fatigue',
    'Vitamin D3': 'contributes to the maintenance of normal bones and normal immune function',
    'Vitamin E': 'contributes to the protection of cells from oxidative stress',
    'Vitamin A': 'contributes to the maintenance of normal skin and normal vision',
    'Vitamin B1': 'contributes to normal energy-yielding metabolism',
    'Vitamin B2': 'contributes to the reduction of tiredness and fatigue',
    'Vitamin B3': 'contributes to normal psychological function and normal skin',
    'Vitamin B5': 'contributes to normal mental performance',
    'Vitamin B6': 'contributes to the regulation of hormonal activity and reduction of tiredness',
    'Vitamin B7': 'contributes to the maintenance of normal hair and skin',
    'Vitamin B9': 'contributes to normal blood formation and the reduction of tiredness',
    'Vitamin B12': 'contributes to normal energy-yielding metabolism and reduction of tiredness',
    'Magnesium': 'contributes to the reduction of tiredness and normal muscle function',
    'Calcium': 'contributes to normal muscle function and neurotransmission',
    'Zinc': 'contributes to the maintenance of normal hair, skin and nails',
    'Selenium': 'contributes to the maintenance of normal hair and nails and normal immune function',
    'Iodine': 'contributes to normal cognitive function and energy-yielding metabolism',
    'Choline': 'contributes to normal lipid and homocysteine metabolism'
  };
  var CLAIM_KEYS = Object.keys(CLAIMS).sort(function (a, b) { return b.length - a.length; });

  function claimFor(name) {
    for (var i = 0; i < CLAIM_KEYS.length; i++) {
      var k = CLAIM_KEYS[i];
      if (new RegExp('^' + k + '\\b').test(name)) return { k: k, claim: CLAIMS[k] };
    }
    return null;
  }

  function renderClaims(node, data) {
    var d = data[node.getAttribute('data-supp-claims')];
    if (!d || !d.actives) { node.hidden = true; return; }
    node.textContent = '';
    var seen = {}, any = false;
    d.actives.forEach(function (a) {
      var m = claimFor(a.name || '');
      if (!m || seen[m.k]) return;
      seen[m.k] = true; any = true;
      var li = el('li');
      li.appendChild(el('strong', null, m.k));
      li.appendChild(document.createTextNode(' ' + m.claim + '.'));
      node.appendChild(li);
    });
    if (!any) node.hidden = true;
  }

  function run() {
    var facts = document.querySelectorAll('[data-supp-facts]');
    var ings = document.querySelectorAll('[data-supp-ingredient]');
    var dirs = document.querySelectorAll('[data-supp-directions]');
    var claims = document.querySelectorAll('[data-supp-claims]');
    if (!facts.length && !ings.length && !dirs.length && !claims.length) return;
    load().then(function (data) {
      facts.forEach(function (n) { renderFacts(n, data); });
      ings.forEach(function (n) { renderIngredient(n, data); });
      dirs.forEach(function (n) { renderDirections(n, data); });
      claims.forEach(function (n) { renderClaims(n, data); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
